// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect } from 'react';
import { getProviderStatusHistory } from '../api/providerStatusApi';
import type { PolicyProviderStatusHistoryDTO } from '../types/providerStatus';

interface UseProviderStatusHistoryReturn {
  readonly data: PolicyProviderStatusHistoryDTO | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refresh: () => Promise<void>;
}

export function useProviderStatusHistory(
  policyId: string,
  enabled = true
): UseProviderStatusHistoryReturn {
  const [data, setData] = useState<PolicyProviderStatusHistoryDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !policyId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getProviderStatusHistory(policyId);
      setData(result);
    } catch (err: unknown) {
      console.error('Failed to fetch provider status history:', err);
      setError('Failed to load provider status history');
    } finally {
      setLoading(false);
    }
  }, [policyId, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
